import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Users,
  TrendingUp,
  Target,
  Activity,
  DollarSign,
  BarChart3,
  PieChart,
  Calendar,
  Plus,
  ArrowUpRight,
} from 'lucide-react'
import { crmDashboardApi, pipelineApi } from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatCurrency } from '@/lib/utils'
import { useState } from 'react'
import { usePermissions } from '@/contexts/PermissionContext'

export default function CRMDashboardPage() {
  const { t } = useTranslation()
  const { canAddEdit } = usePermissions()
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | undefined>()

  // Fetch pipelines for filter
  const { data: pipelines } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => pipelineApi.getPipelines(),
  })

  // Dashboard queries
  const { data: leadFunnel, isLoading: loadingFunnel } = useQuery({
    queryKey: ['crm-dashboard', 'lead-funnel'],
    queryFn: () => crmDashboardApi.getLeadFunnel(),
  })

  const { data: pipelineOverview, isLoading: loadingPipeline } = useQuery({
    queryKey: ['crm-dashboard', 'pipeline-overview', selectedPipelineId],
    queryFn: () => crmDashboardApi.getPipelineOverview({ pipeline_id: selectedPipelineId }),
  })

  const { data: activitySummary, isLoading: loadingActivity } = useQuery({
    queryKey: ['crm-dashboard', 'activity-summary'],
    queryFn: () => crmDashboardApi.getActivitySummary(),
  })

  const { data: salesForecast, isLoading: loadingForecast } = useQuery({
    queryKey: ['crm-dashboard', 'sales-forecast', selectedPipelineId],
    queryFn: () => crmDashboardApi.getSalesForecast({ pipeline_id: selectedPipelineId, months: 6 }),
  })

  const { data: leadSources, isLoading: loadingSources } = useQuery({
    queryKey: ['crm-dashboard', 'lead-sources'],
    queryFn: () => crmDashboardApi.getLeadSourceStats(),
  })

  const { data: dealsByOwner, isLoading: loadingOwners } = useQuery({
    queryKey: ['crm-dashboard', 'deals-by-owner', selectedPipelineId],
    queryFn: () => crmDashboardApi.getDealsByOwner({ pipeline_id: selectedPipelineId }),
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('crm.dashboard.title', 'CRM Dashboard')}</h1>
          <p className="text-muted-foreground">
            {t('crm.dashboard.subtitle', 'Overview of your sales pipeline and lead management')}
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedPipelineId || 'all'} onValueChange={(v) => setSelectedPipelineId(v === 'all' ? undefined : v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('crm.dashboard.allPipelines', 'All Pipelines')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('crm.dashboard.allPipelines', 'All Pipelines')}</SelectItem>
              {pipelines?.map((p) => (
                <SelectItem key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canAddEdit('CRM_LEADS') && (
            <Link to="/crm/leads/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {t('crm.leads.newLead', 'New Lead')}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('crm.dashboard.totalLeads', 'Total Leads')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leadFunnel?.total_leads || 0}</div>
            <p className="text-xs text-muted-foreground">
              {t('crm.dashboard.inPipeline', 'In pipeline')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('crm.dashboard.totalDeals', 'Total Deals')}</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pipelineOverview?.total_deals || 0}</div>
            <p className="text-xs text-muted-foreground">
              {t('crm.dashboard.activeDeals', 'Active deals')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('crm.dashboard.pipelineValue', 'Pipeline Value')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(pipelineOverview?.total_value || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {t('crm.dashboard.weightedValue', 'Weighted')}: {formatCurrency(pipelineOverview?.weighted_value || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('crm.dashboard.winRate', 'Win Rate')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(pipelineOverview?.win_rate || 0).toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {t('crm.dashboard.avgDaysToClose', 'Avg')}: {pipelineOverview?.avg_days_to_close || 0} {t('common.days', 'days')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Lead Funnel */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              {t('crm.dashboard.leadFunnel', 'Lead Funnel')}
            </CardTitle>
            <CardDescription>{t('crm.dashboard.leadsByStatus', 'Leads by status')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingFunnel ? (
              <div className="text-center py-4 text-muted-foreground">{t('common.loading', 'Loading...')}</div>
            ) : (
              <div className="space-y-3">
                {leadFunnel?.funnel?.map((item) => (
                  <div key={item.status_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.status_color }} />
                      <span className="text-sm">{item.status_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.count}</span>
                      <span className="text-xs text-muted-foreground">({item.percentage.toFixed(1)}%)</span>
                    </div>
                  </div>
                ))}
                {(!leadFunnel?.funnel || leadFunnel.funnel.length === 0) && (
                  <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline Stages */}
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {t('crm.dashboard.pipelineStages', 'Pipeline Stages')}
            </CardTitle>
            <CardDescription>{t('crm.dashboard.dealsByStage', 'Deals by stage')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingPipeline ? (
              <div className="text-center py-4 text-muted-foreground">{t('common.loading', 'Loading...')}</div>
            ) : (
              <div className="space-y-3">
                {pipelineOverview?.stages?.map((stage) => (
                  <div key={stage.stage_id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.stage_color }} />
                        <span>{stage.stage_name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-medium">{stage.deal_count} deals</span>
                        <span className="text-muted-foreground">{formatCurrency(stage.total_value)}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          backgroundColor: stage.stage_color,
                          width: `${pipelineOverview.total_deals > 0 ? (stage.deal_count / pipelineOverview.total_deals) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
                {(!pipelineOverview?.stages || pipelineOverview.stages.length === 0) && (
                  <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Summary */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t('crm.dashboard.activitySummary', 'Activity Summary')}
            </CardTitle>
            <CardDescription>{t('crm.dashboard.recentActivity', 'Recent activity overview')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingActivity ? (
              <div className="text-center py-4 text-muted-foreground">{t('common.loading', 'Loading...')}</div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{activitySummary?.activities_today || 0}</div>
                    <div className="text-xs text-muted-foreground">{t('crm.dashboard.today', 'Today')}</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{activitySummary?.activities_this_week || 0}</div>
                    <div className="text-xs text-muted-foreground">{t('crm.dashboard.thisWeek', 'This Week')}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('crm.dashboard.pendingTasks', 'Pending Tasks')}</span>
                    <span className="font-medium">{activitySummary?.pending_tasks || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-destructive">{t('crm.dashboard.overdueTasks', 'Overdue Tasks')}</span>
                    <span className="font-medium text-destructive">{activitySummary?.overdue_tasks || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">{t('crm.dashboard.completedTasks', 'Completed Tasks')}</span>
                    <span className="font-medium text-green-600">{activitySummary?.completed_tasks || 0}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sales Forecast */}
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t('crm.dashboard.salesForecast', 'Sales Forecast')}
            </CardTitle>
            <CardDescription>{t('crm.dashboard.expectedRevenue', 'Expected revenue by month')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingForecast ? (
              <div className="text-center py-4 text-muted-foreground">{t('common.loading', 'Loading...')}</div>
            ) : (
              <div className="space-y-3">
                {salesForecast?.forecasts?.map((forecast) => (
                  <div key={forecast.month} className="flex items-center justify-between">
                    <span className="text-sm font-medium">{forecast.month}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm">{forecast.deal_count} deals</span>
                      <span className="font-medium">{formatCurrency(forecast.expected_value)}</span>
                      <span className="text-xs text-muted-foreground">
                        ({t('crm.dashboard.weighted', 'Weighted')}: {formatCurrency(forecast.weighted_value)})
                      </span>
                    </div>
                  </div>
                ))}
                {(!salesForecast?.forecasts || salesForecast.forecasts.length === 0) && (
                  <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                )}
                {salesForecast && (
                  <div className="pt-3 border-t mt-3">
                    <div className="flex justify-between font-medium">
                      <span>{t('crm.dashboard.totalForecast', 'Total Forecast')}</span>
                      <span>{formatCurrency(salesForecast.total_expected)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Lead Sources */}
        <Card>
          <CardHeader>
            <CardTitle>{t('crm.dashboard.leadSources', 'Lead Sources')}</CardTitle>
            <CardDescription>{t('crm.dashboard.conversionBySource', 'Conversion rate by source')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSources ? (
              <div className="text-center py-4 text-muted-foreground">{t('common.loading', 'Loading...')}</div>
            ) : (
              <div className="space-y-3">
                {leadSources?.sources?.slice(0, 5).map((source) => (
                  <div key={source.source_id} className="flex items-center justify-between">
                    <span className="text-sm">{source.source_name}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm">{source.lead_count} leads</span>
                      <span className="text-sm font-medium text-green-600">{source.conversion_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
                {(!leadSources?.sources || leadSources.sources.length === 0) && (
                  <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card>
          <CardHeader>
            <CardTitle>{t('crm.dashboard.topPerformers', 'Top Performers')}</CardTitle>
            <CardDescription>{t('crm.dashboard.dealsByOwner', 'Deals by owner')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingOwners ? (
              <div className="text-center py-4 text-muted-foreground">{t('common.loading', 'Loading...')}</div>
            ) : (
              <div className="space-y-3">
                {dealsByOwner?.owners?.slice(0, 5).map((owner) => (
                  <div key={owner.user_id} className="flex items-center justify-between">
                    <span className="text-sm font-medium">{owner.user_name}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm">{owner.deal_count} deals</span>
                      <span className="text-sm">{formatCurrency(owner.total_value)}</span>
                      <span className="text-sm font-medium text-green-600">{owner.win_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
                {(!dealsByOwner?.owners || dealsByOwner.owners.length === 0) && (
                  <div className="text-center py-4 text-muted-foreground">{t('common.noData', 'No data')}</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 md:grid-cols-4">
        <Link to="/crm/leads">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-medium">{t('crm.leads.title', 'Leads')}</span>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/crm/deals">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Target className="h-5 w-5 text-primary" />
                <span className="font-medium">{t('crm.deals.title', 'Deals')}</span>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/crm/activities">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-primary" />
                <span className="font-medium">{t('crm.activities.title', 'Activities')}</span>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/crm/reports">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-primary" />
                <span className="font-medium">{t('crm.reports.title', 'Reports')}</span>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  )
}

